/**
 * /perspektive — Tag der Perspektive: Kontaktdaten in 20 Sekunden.
 *
 * Ein Formular für zwei Wege am Stand:
 *  - /perspektive        Besucher scannen den QR-Code und tragen sich selbst ein. Danach
 *                        zeigen sie den Bestätigungs-Bildschirm am ZBA-Tisch vor.
 *  - /perspektive?team   ZBA-Mitarbeiter erfassen am Tisch. Nach dem Absenden geht es mit
 *                        einem Tipp zur nächsten Person, ein Zähler zeigt die heutige Zahl.
 *
 * Server: POST /api/perspektive → event_leads + Pipedrive-Deal "(Tag der Perspektive)".
 */
import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

const LOGO = 'https://zba.academy/webinar/images/zbalogo2.png';
const EMPTY = { vorname: '', nachname: '', telefon: '', email: '', kundennummer: '', consent: false, hp_xq7: '' };
const COUNT_KEY = 'zba_tdp_count';

function readCount() {
  try {
    const v = JSON.parse(localStorage.getItem(COUNT_KEY) || 'null');
    const today = new Date().toDateString();
    return v && v.day === today ? v.n : 0;
  } catch { return 0; }
}
function writeCount(n) {
  try { localStorage.setItem(COUNT_KEY, JSON.stringify({ day: new Date().toDateString(), n })); } catch {}
}

const CSS = `
.tdp { min-height: 100vh; min-height: 100dvh; background: #F0F6FB; color: #1e2b38; font-family: system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif; display: flex; flex-direction: column; }
.tdp * { box-sizing: border-box; }
.tdp-wrap { width: 100%; max-width: 520px; margin: 0 auto; padding: 16px 16px 28px; flex: 1; }
@media (min-width: 640px) { .tdp-wrap { padding: 40px 24px; } }

.tdp-top { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 18px; }
.tdp-logo { height: 32px; width: auto; display: block; }
.tdp-chip { display: inline-flex; align-items: center; gap: 6px; padding: 6px 12px; border-radius: 100px; background: #fff; border: 1px solid #dbe6f0; font-size: 12px; font-weight: 700; color: #3E4954; white-space: nowrap; }
.tdp-chip i { width: 7px; height: 7px; border-radius: 50%; background: #E8530E; }
.tdp-chip.team { background: #3E4954; border-color: #3E4954; color: #fff; }
.tdp-chip.team i { background: #FF4500; }

.tdp-h1 { font-size: 27px; line-height: 1.15; font-weight: 800; letter-spacing: -.02em; margin: 0 0 8px; text-wrap: balance; }
.tdp-h1 em { font-style: normal; color: #E8530E; }
@media (min-width: 640px) { .tdp-h1 { font-size: 32px; } }
.tdp-sub { font-size: 15.5px; line-height: 1.5; color: #4b5a69; margin: 0 0 18px; }

.tdp-card { background: #fff; border-radius: 22px; padding: 20px 18px; box-shadow: 0 10px 30px rgba(62,73,84,.10), 0 1px 3px rgba(62,73,84,.08); }
@media (min-width: 640px) { .tdp-card { padding: 28px; } }

.tdp-row { display: grid; grid-template-columns: 1fr; gap: 14px; }
@media (min-width: 400px) { .tdp-row.two { grid-template-columns: 1fr 1fr; } }
.tdp-field { margin-bottom: 14px; }
.tdp-row .tdp-field { margin-bottom: 0; }
.tdp-row { margin-bottom: 14px; }
.tdp-label { display: flex; justify-content: space-between; align-items: baseline; gap: 8px; font-size: 13px; font-weight: 700; color: #3E4954; margin: 0 0 6px; }
.tdp-label span { font-weight: 500; color: #8a97a8; font-size: 12px; }
.tdp-input { width: 100%; height: 54px; padding: 0 15px; border: 1.5px solid #d5dee8; border-radius: 14px; font: inherit; font-size: 17px; color: #1e2b38; background: #fff; outline: none; -webkit-appearance: none; transition: border-color .15s, box-shadow .15s; }
.tdp-input::placeholder { color: #a7b2bf; }
.tdp-input:focus { border-color: #3E4954; box-shadow: 0 0 0 4px rgba(62,73,84,.12); }
.tdp-input.err { border-color: #dc2626; box-shadow: 0 0 0 4px rgba(220,38,38,.10); }
.tdp-help { font-size: 12.5px; color: #6b7a8d; margin: 6px 0 0; line-height: 1.4; }
.tdp-error { font-size: 13px; color: #dc2626; font-weight: 600; margin: 6px 0 0; }

.tdp-agree { display: flex; gap: 12px; align-items: flex-start; padding: 14px; border-radius: 14px; background: #F0F6FB; border: 1.5px solid transparent; cursor: pointer; margin: 4px 0 16px; -webkit-tap-highlight-color: transparent; }
.tdp-agree.err { border-color: #dc2626; }
.tdp-agree input { position: absolute; opacity: 0; pointer-events: none; }
.tdp-box { flex: 0 0 24px; width: 24px; height: 24px; border-radius: 7px; border: 2px solid #9fb0c2; background: #fff; display: flex; align-items: center; justify-content: center; transition: all .15s; }
.tdp-agree.on .tdp-box { background: #E8530E; border-color: #E8530E; }
.tdp-agree .txt { font-size: 13.5px; line-height: 1.45; color: #3E4954; }
.tdp-agree a { color: #3E4954; font-weight: 700; }

.tdp-btn { width: 100%; height: 58px; border: 0; border-radius: 16px; background: #E8530E; color: #fff; font: inherit; font-size: 18px; font-weight: 800; letter-spacing: .01em; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 10px; box-shadow: 0 8px 20px rgba(232,83,14,.28); transition: transform .1s, background .15s; -webkit-tap-highlight-color: transparent; }
.tdp-btn:hover { background: #FF4500; }
.tdp-btn:active { transform: scale(.985); }
.tdp-btn:disabled { opacity: .7; cursor: wait; }
.tdp-btn.dark { background: #3E4954; box-shadow: 0 8px 20px rgba(62,73,84,.25); }
.tdp-btn.dark:hover { background: #2f3943; }
.spin { width: 18px; height: 18px; border: 2.5px solid rgba(255,255,255,.4); border-top-color: #fff; border-radius: 50%; animation: tdpspin .7s linear infinite; }
@keyframes tdpspin { to { transform: rotate(360deg); } }

.tdp-done { text-align: center; }
.tdp-check { width: 84px; height: 84px; border-radius: 50%; background: #e7f6ec; display: flex; align-items: center; justify-content: center; margin: 4px auto 16px; animation: tdppop .35s cubic-bezier(.2,1.4,.4,1); }
@keyframes tdppop { from { transform: scale(.5); opacity: 0; } to { transform: scale(1); opacity: 1; } }
.tdp-done h2 { font-size: 26px; font-weight: 800; margin: 0 0 8px; letter-spacing: -.01em; }
.tdp-done p { font-size: 15.5px; color: #4b5a69; line-height: 1.5; margin: 0 0 18px; }
.tdp-show { display: inline-block; background: #fff4ec; color: #b43f08; font-weight: 700; padding: 10px 14px; border-radius: 12px; font-size: 14.5px; margin-bottom: 18px; }
.tdp-sum { text-align: left; background: #F0F6FB; border-radius: 16px; padding: 6px 16px; margin: 0 0 18px; }
.tdp-sum div { display: flex; justify-content: space-between; gap: 12px; padding: 10px 0; border-bottom: 1px solid #dde7f0; font-size: 15px; }
.tdp-sum div:last-child { border-bottom: 0; }
.tdp-sum dt { color: #6b7a8d; font-weight: 600; flex-shrink: 0; }
.tdp-sum dd { margin: 0; font-weight: 700; text-align: right; word-break: break-word; }
.tdp-sum dd.miss { color: #a7b2bf; font-weight: 500; }
.tdp-ref { font-size: 11px; color: #a3adb9; margin-top: 14px; }

.tdp-foot { text-align: center; font-size: 12px; color: #8a97a8; padding: 0 16px 22px; line-height: 1.7; }
.tdp-foot a { color: #6b7a8d; }
`;

const CheckIcon = ({ size = 14, color = '#fff', w = 3 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={w} strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
);

export default function PerspektivePage() {
  const location = useLocation();
  const team = new URLSearchParams(location.search).has('team');

  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [done, setDone] = useState(null);          // { ref, duplicate, data }
  const [count, setCount] = useState(readCount);
  const first = useRef(null);
  const refs = { nachname: useRef(null), telefon: useRef(null), email: useRef(null), kundennummer: useRef(null) };

  useEffect(() => { document.title = 'Tag der Perspektive | ZBA Academy'; }, []);

  const set = (k, v) => {
    setForm(f => ({ ...f, [k]: v }));
    if (errors[k]) setErrors(e => ({ ...e, [k]: undefined }));
  };

  // Enter springt zum nächsten Feld statt das Formular abzuschicken
  const nextOn = (key) => (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (key) refs[key].current?.focus(); else e.currentTarget.blur();
  };

  const validate = () => {
    const e = {};
    if (!form.vorname.trim()) e.vorname = 'Bitte Vornamen eintragen.';
    if (!form.nachname.trim()) e.nachname = 'Bitte Nachnamen eintragen.';
    if (form.telefon.replace(/\D/g, '').length < 6) e.telefon = 'Bitte Telefonnummer eintragen.';
    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim())) e.email = 'Diese E-Mail-Adresse sieht nicht richtig aus.';
    if (!form.consent) e.consent = 'Bitte bestätigen.';
    return e;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    setSubmitError('');
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) return;
    setLoading(true);
    try {
      const res = await fetch('/api/perspektive', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, erfasst: team ? 'team' : 'selbst' }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        if (data.errors) { setErrors(data.errors); return; }
        throw new Error(data.error || 'Das hat nicht geklappt. Bitte noch einmal versuchen.');
      }
      if (!data.duplicate) { const n = count + 1; setCount(n); writeCount(n); }
      setDone({ ref: data.ref, duplicate: !!data.duplicate, data: form });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setSubmitError(err.message === 'Failed to fetch' ? 'Keine Internetverbindung. Bitte prüfen und noch einmal tippen.' : err.message);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setForm(EMPTY); setErrors({}); setSubmitError(''); setDone(null);
    setTimeout(() => first.current?.focus(), 50);
  };

  const d = done?.data;

  return (
    <div className="tdp">
      <style>{CSS}</style>
      <div className="tdp-wrap">
        <div className="tdp-top">
          <img src={LOGO} alt="ZBA Academy" className="tdp-logo" />
          {team
            ? <span className="tdp-chip team"><i />Team · {count} heute</span>
            : <span className="tdp-chip"><i />Tag der Perspektive</span>}
        </div>

        {!done && (
          <>
            <h1 className="tdp-h1">{team ? <>Neue Person <em>erfassen</em></> : <>Ihr Weg in einen <em>neuen Beruf</em></>}</h1>
            <p className="tdp-sub">
              {team
                ? 'Nur Name und Telefon sind Pflicht. E-Mail und Kundennummer, wenn vorhanden.'
                : 'Tragen Sie kurz Ihre Kontaktdaten ein – wir schicken Ihnen Ihr persönliches Bildungsangebot. Kostenlos und unverbindlich.'}
            </p>

            <form className="tdp-card" onSubmit={submit} noValidate>
              <div className="tdp-row two">
                <div className="tdp-field">
                  <label className="tdp-label" htmlFor="tdp-vorname">Vorname</label>
                  <input ref={first} id="tdp-vorname" className={`tdp-input${errors.vorname ? ' err' : ''}`} value={form.vorname} onChange={e => set('vorname', e.target.value)} onKeyDown={nextOn('nachname')} autoComplete={team ? 'off' : 'given-name'} autoCapitalize="words" enterKeyHint="next" />
                  {errors.vorname && <p className="tdp-error">{errors.vorname}</p>}
                </div>
                <div className="tdp-field">
                  <label className="tdp-label" htmlFor="tdp-nachname">Nachname</label>
                  <input ref={refs.nachname} id="tdp-nachname" className={`tdp-input${errors.nachname ? ' err' : ''}`} value={form.nachname} onChange={e => set('nachname', e.target.value)} onKeyDown={nextOn('telefon')} autoComplete={team ? 'off' : 'family-name'} autoCapitalize="words" enterKeyHint="next" />
                  {errors.nachname && <p className="tdp-error">{errors.nachname}</p>}
                </div>
              </div>

              <div className="tdp-field">
                <label className="tdp-label" htmlFor="tdp-telefon">Telefonnummer</label>
                <input ref={refs.telefon} id="tdp-telefon" type="tel" inputMode="tel" className={`tdp-input${errors.telefon ? ' err' : ''}`} value={form.telefon} onChange={e => set('telefon', e.target.value)} onKeyDown={nextOn('email')} placeholder="0151 23456789" autoComplete={team ? 'off' : 'tel'} enterKeyHint="next" />
                {errors.telefon && <p className="tdp-error">{errors.telefon}</p>}
              </div>

              <div className="tdp-field">
                <label className="tdp-label" htmlFor="tdp-email">E-Mail <span>optional</span></label>
                <input ref={refs.email} id="tdp-email" type="email" inputMode="email" className={`tdp-input${errors.email ? ' err' : ''}`} value={form.email} onChange={e => set('email', e.target.value)} onKeyDown={nextOn('kundennummer')} placeholder="name@beispiel.de" autoComplete={team ? 'off' : 'email'} autoCapitalize="none" autoCorrect="off" spellCheck={false} enterKeyHint="next" />
                {errors.email && <p className="tdp-error">{errors.email}</p>}
              </div>

              <div className="tdp-field">
                <label className="tdp-label" htmlFor="tdp-kdnr">Kundennummer Agentur / Jobcenter <span>optional</span></label>
                <input ref={refs.kundennummer} id="tdp-kdnr" className="tdp-input" value={form.kundennummer} onChange={e => set('kundennummer', e.target.value)} onKeyDown={nextOn(null)} placeholder="z. B. 123A456789" autoComplete="off" autoCapitalize="characters" autoCorrect="off" spellCheck={false} enterKeyHint="done" />
                {!team && <p className="tdp-help">Steht oben auf jedem Brief vom Amt. Nicht zur Hand? Einfach frei lassen.</p>}
              </div>

              {/* Honeypot gegen Bots — für Menschen und Autofill unsichtbar */}
              <div style={{ display: 'none' }} aria-hidden="true">
                <input type="text" name="hp_xq7" value={form.hp_xq7} onChange={e => set('hp_xq7', e.target.value)} tabIndex={-1} autoComplete="off" data-lpignore="true" data-1p-ignore="true" data-bwignore="true" />
              </div>

              <label className={`tdp-agree${form.consent ? ' on' : ''}${errors.consent ? ' err' : ''}`}>
                <input type="checkbox" checked={form.consent} onChange={e => set('consent', e.target.checked)} />
                <span className="tdp-box" aria-hidden="true">{form.consent && <CheckIcon />}</span>
                <span className="txt">
                  {team ? 'Die Person ist einverstanden' : 'Ich bin einverstanden'}, dass die ZBA Academy die Daten speichert, {team ? 'sie' : 'mich'} zu einem Bildungsangebot kontaktiert und dafür bei Bedarf mit der Agentur für Arbeit bzw. dem Jobcenter abstimmt.{' '}
                  <a href="https://zba.academy/datenschutz" target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>Datenschutz</a>
                </span>
              </label>

              {submitError && <p className="tdp-error" style={{ margin: '0 0 12px' }}>{submitError}</p>}
              <button type="submit" className="tdp-btn" disabled={loading}>
                {loading ? <><span className="spin" />Wird gespeichert…</> : 'Absenden'}
              </button>
            </form>
          </>
        )}

        {done && (
          <div className="tdp-card tdp-done">
            <div className="tdp-check"><CheckIcon size={42} color="#16a34a" w={3.2} /></div>
            <h2>{done.duplicate ? 'Schon erfasst' : team ? 'Gespeichert' : `Danke, ${d.vorname.trim()}!`}</h2>
            <p>
              {done.duplicate
                ? 'Diese Person ist bereits eingetragen. Neue Angaben wurden ergänzt.'
                : team
                  ? `${d.vorname.trim()} ${d.nachname.trim()} ist im System.`
                  : 'Wir melden uns mit Ihrem persönlichen Bildungsangebot.'}
            </p>
            {!team && !done.duplicate && <div className="tdp-show">👉 Bitte zeigen Sie diesen Bildschirm kurz am ZBA-Tisch.</div>}

            <dl className="tdp-sum">
              <div><dt>Name</dt><dd>{d.vorname.trim()} {d.nachname.trim()}</dd></div>
              <div><dt>Telefon</dt><dd>{d.telefon.trim()}</dd></div>
              <div><dt>E-Mail</dt><dd className={d.email.trim() ? '' : 'miss'}>{d.email.trim() || '—'}</dd></div>
              <div><dt>Kundennr.</dt><dd className={d.kundennummer.trim() ? '' : 'miss'}>{d.kundennummer.toUpperCase().replace(/[\s\-/]/g, '') || '—'}</dd></div>
            </dl>

            <button type="button" className={`tdp-btn${team ? '' : ' dark'}`} onClick={reset}>
              {team ? 'Nächste Person →' : 'Weitere Person eintragen'}
            </button>
            {done.ref && <p className="tdp-ref">Referenz: {done.ref}</p>}
          </div>
        )}
      </div>

      <footer className="tdp-foot">
        ZBA – Zentrale Brandschutztechnik-Akademie GmbH<br />
        <a href="https://zba.academy/impressum" target="_blank" rel="noopener noreferrer">Impressum</a>
        <span style={{ margin: '0 8px', opacity: .5 }}>·</span>
        <a href="https://zba.academy/datenschutz" target="_blank" rel="noopener noreferrer">Datenschutz</a>
      </footer>
    </div>
  );
}
